import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { copyToClipboard } from '../utils/storage';
import { formatFileSize } from '../utils/api';

function ShareLink({ shareLink, filename, fileCount, totalSize, uploadStatus, onNewUpload }) {
  const [copied, setCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [showQR, setShowQR] = useState(true);
  const canvasRef = useRef(null);

  // Generate QR Code when component mounts
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(shareLink, {
          width: 200,
          margin: 2,
          color: {
            dark: '#1e293b',  // Dark color for QR code
            light: '#ffffff'  // Light background for QR code
          }
        });
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    if (shareLink) {
      generateQRCode();
    }
  }, [shareLink]);

  const handleCopy = async () => {
    const success = await copyToClipboard(shareLink);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-code-${filename || 'share'}.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#242938', 
      border: '1px solid #374151',
      borderRadius: '2px',
      padding: '24px'
    }}>
      {/* Success Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ 
          backgroundColor: '#10b98120', 
          borderRadius: '50%', 
          width: '48px', 
          height: '48px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          margin: '0 auto 16px',
          border: '1px solid #10b98140'
        }}>
          <svg width="24" height="24" style={{ color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#e2e8f0', 
          marginBottom: '8px' 
        }}>
          Upload Successful!
        </h3>
        
        <p style={{ color: '#9ca3af', fontSize: '14px' }}>
          {fileCount && fileCount > 1
            ? `${fileCount} files (${formatFileSize(totalSize)}) uploaded successfully.`
            : 'Your file(s) has been uploaded successfully.'}
        </p>
      </div>

      {/* Share Link Section */}
      <div style={{ 
        backgroundColor: '#1e293b', 
        border: '1px solid #334155',
        borderRadius: '2px',
        padding: '16px', 
        marginBottom: '24px'
      }}>
        <label style={{ 
          display: 'block', 
          fontSize: '12px', 
          fontWeight: '500', 
          color: '#e2e8f0', 
          marginBottom: '8px' 
        }}>
          Share Link
        </label>
        
        <div style={{ display: 'flex' }}>
          <input
            type="text"
            value={shareLink}
            readOnly
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRight: 'none',
              borderRadius: '2px 0 0 2px',
              color: '#e2e8f0',
              fontSize: '12px',
              outline: 'none'
            }}
          />
          
          <button
            onClick={handleCopy}
            style={{
              padding: '8px 12px',
              backgroundColor: copied ? '#10b981' : '#4b5563',
              border: '1px solid ' + (copied ? '#10b981' : '#4b5563'),
              borderRadius: '0 2px 2px 0',
              color: '#fff',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.target.style.backgroundColor = '#6b7280';
                e.target.style.borderColor = '#6b7280';
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.target.style.backgroundColor = '#4b5563';
                e.target.style.borderColor = '#4b5563';
              }
            }}
          >
            {copied ? (
              <>
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Google Ads Unit removed for AdSense compliance - no ads near download content */}

      {/* QR Code Section */}
      <div style={{ 
        backgroundColor: '#1e293b', 
        border: '1px solid #334155',
        borderRadius: '2px',
        padding: '16px', 
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: '#e2e8f0'
          }}>
            QR Code
          </label>
          <button
            onClick={() => setShowQR(!showQR)}
            style={{
              backgroundColor: 'transparent',
              color: '#60a5fa',
              border: 'none',
              fontSize: '12px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {showQR ? 'Hide' : 'Show'} QR Code
          </button>
        </div>

        {showQR && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center' 
          }}>
            {qrCodeDataUrl ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code for share link"
                  style={{ 
                    width: '200px', 
                    height: '200px',
                    backgroundColor: '#fff',
                    padding: '8px',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    display: 'block',
                    margin: '0 auto 12px auto'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    onClick={handleDownloadQR}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '2px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                  >
                    Download QR Code
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                Generating QR code...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Important Information */}
      <div style={{ 
        backgroundColor: '#1e40af20', 
        border: '1px solid #3b82f640',
        borderRadius: '2px',
        padding: '16px', 
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ marginRight: '12px', marginTop: '2px' }}>
            <svg width="16" height="16" style={{ color: '#60a5fa' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 style={{ 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#60a5fa',
              marginBottom: '8px'
            }}>
              Important Information
            </h3>
            <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
                              <div style={{ marginBottom: '4px' }}>• This link will expire after 5 days</div>
              <div style={{ marginBottom: '4px' }}>• Anyone with this link can download your files</div>
              <div>• QR code allows easy mobile access</div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => window.open(shareLink, '_blank')}
          style={{
            flex: 1,
            backgroundColor: '#3b82f6',
            color: '#fff',
            padding: '12px 16px',
            border: 'none',
            borderRadius: '2px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          View Files
        </button>
        
        <button
          onClick={onNewUpload}
          style={{
            flex: 1,
            backgroundColor: 'transparent',
            color: '#9ca3af',
            border: '1px solid #374151',
            padding: '12px 16px',
            borderRadius: '2px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#6b7280';
            e.target.style.color = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#374151';
            e.target.style.color = '#9ca3af';
          }}
        >
          Upload Another File
        </button>
      </div>
    </div>
  );
}

export default ShareLink; 